import React, { useState, useImperativeHandle, forwardRef } from "react";
import { EditorState, convertToRaw, convertFromRaw } from "draft-js";
import { Editor } from "react-draft-wysiwyg";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import axios from "axios";
import useLocalStorage from "../hooks/useLocalStorage";
import draftToHtml from "draftjs-to-html";

interface EditorContainerProps {
  initialContent?: string; // Optional prop to initialize the editor with content
  onContentChange?: (htmlContent: string) => void; // Callback to handle content changes
  theme?: "light" | "dark"; // Theme prop to toggle between light and dark modes
}

const EditorContainer = forwardRef(
  (
    { initialContent = "", onContentChange, theme = "light" }: EditorContainerProps,
    ref
  ) => {
    // Use local storage to persist editor state
    const [storedEditorState, setStoredEditorState] = useLocalStorage<string>(
      "editorState",
      initialContent
        ? JSON.stringify(
            convertToRaw(
              EditorState.createWithContent(
                convertFromRaw(JSON.parse(initialContent))
              ).getCurrentContent()
            )
          )
        : undefined
    );

    // Initialize editor state from local storage or create an empty state
    const [editorState, setEditorState] = useState(() =>
      storedEditorState
        ? EditorState.createWithContent(convertFromRaw(JSON.parse(storedEditorState)))
        : EditorState.createEmpty()
    );

    // Expose the clearEditor method to the parent component
    useImperativeHandle(ref, () => ({
      clearEditor() {
        setEditorState(EditorState.createEmpty()); // Reset editor state
        setStoredEditorState(""); // Clear local storage
      },
    }));

    // Handle editor state changes
    const onChange = (state: EditorState) => {
      setEditorState(state);

      // Save editor state to local storage
      const rawContent = convertToRaw(state.getCurrentContent());
      setStoredEditorState(JSON.stringify(rawContent));

      // Convert editor content to HTML and call the onContentChange callback if provided
      if (onContentChange) {
        const htmlContent = draftToHtml(rawContent);
        onContentChange(htmlContent);
      }
    };

    // Image upload callback
    const uploadImageCallback = async (
      file: File
    ): Promise<{ data: { link: string } }> => {
      const formData = new FormData();
      formData.append("image", file);

      try {
        const response = await axios.post(
          "https://geopsyresearch.org/ttwca-server/upload_image.php",
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data", // Explicitly set the content type
            },
          }
        );

        if (response.status === 200 && response.data.filePath) {
          return {
            data: {
              link: `https://geopsyresearch.org/ttwca-server/${response.data.filePath}`, // Construct the full URL
            },
          };
        } else {
          throw new Error(response.data.message || "Image upload failed");
        }
      } catch (error) {
        console.error("Image upload error:", error);
        return Promise.reject(error);
      }
    };

    const editorClass =
      theme === "dark" ? "editor-container-dark" : "editor-container-light";
    const toolbarClass = theme === "dark" ? "toolbar-dark" : "toolbar-light";

    return (
      <div
      style={{ zIndex: 0 }}
        className={`editor ${
          theme === "dark" ? "bg-gray-800 text-white" : "bg-white text-black"
        }`}
      >
        <Editor
          editorClassName={`${editorClass} border p-2 rounded`}
          toolbarClassName={toolbarClass}
          editorState={editorState}
          onEditorStateChange={onChange}
          toolbar={{
            inline: { inDropdown: true },
            list: { inDropdown: true },
            textAlign: { inDropdown: true },
            link: { inDropdown: true },
            history: { inDropdown: true },
            image: {
              uploadCallback: uploadImageCallback,
              previewImage: true,
              alt: { present: true, mandatory: true },
            },
          }}
        />
      </div>
    );
  }
);

EditorContainer.displayName = "EditorContainer";

export default EditorContainer;